import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Activity, Users, Moon, RefreshCw, CircleOff, BellRing, Mail, Send } from "lucide-react";
import { toast } from "sonner";
import { getPlatformUsage, runDormantTenantCheck, getDormantAlerts, getReengageDraft, sendReengageEmail } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { formatDate } from "@/lib/dates";
import { Sparkline } from "@/components/Sparkline";

const STATUS_CLS = {
  active: "badge-ok",
  low: "badge-warn",
  dormant: "badge-bad",
  never: "bg-secondary text-muted-foreground border border-border",
};

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 16).replace("T", " ");
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  const date = `${formatDate(iso)} ${iso.slice(11, 16)}`;
  return days <= 0 ? date : `${date} (${days}d)`;
};

/** Sparkline + total with a per-week breakdown tooltip. */
const TrendCell = ({ values, total, weeks, weekLabel, title, totalLabel, emptyLabel, testId, neutral }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <div className="inline-flex cursor-default items-center gap-2" data-testid={testId}>
        <Sparkline values={values || []} testId={`${testId}-sparkline`} tone={neutral ? "neutral" : "auto"} />
        <span className={`font-mono text-[11px] tabular-nums ${total ? "" : "text-muted-foreground"}`}>{total ?? 0}</span>
      </div>
    </TooltipTrigger>
    <TooltipContent className="p-3">
      <div className="mb-1.5 text-xs font-semibold">{title}</div>
      {total ? (
        <div className="grid grid-cols-[auto_auto] gap-x-4 gap-y-0.5 text-xs">
          {(values || []).map((v, i) => (
            <React.Fragment key={i}>
              <span className="opacity-75">{weekLabel(i)}</span>
              <span className="text-right font-mono tabular-nums">{v}</span>
            </React.Fragment>
          ))}
        </div>
      ) : <div className="text-xs opacity-75">{emptyLabel}</div>}
      <div className="mt-1.5 border-t border-current/20 pt-1.5 text-[11px] opacity-75">{totalLabel}</div>
    </TooltipContent>
  </Tooltip>
);

/** "We miss you" email to a quiet tenant's admin(s): pre-filled, editable, sent by the superadmin. */
const ReengageDialog = ({ org, onClose, onSent }) => {
  const { t } = useT();
  const [draft, setDraft] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!org) return;
    setDraft(null);
    getReengageDraft(org.id)
      .then((d) => { setDraft(d); setSubject(d.subject); setBody(d.body); })
      .catch(() => { toast.error(t("usage.reengageDraftError")); onClose(); });
  }, [org]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    if (!draft?.to?.length) { toast.error(t("usage.reengageNoRecipient")); return; }
    setSending(true);
    try {
      const r = await sendReengageEmail(org.id, { subject, body, to: draft.to });
      toast.success(t("usage.reengageSent", { emails: r.sent_to.join(", ") }));
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("usage.reengageFailed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!org} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="reengage-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="h-4 w-4" /> {t("usage.reengageTitle")}</DialogTitle>
          <DialogDescription>{t("usage.reengageDesc", { name: org?.name || "", replyTo: draft?.reply_to || "" })}</DialogDescription>
        </DialogHeader>
        {!draft ? (
          <div className="space-y-3"><Skeleton className="h-9 w-full" /><Skeleton className="h-9 w-full" /><Skeleton className="h-56 w-full" /></div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {draft.days_quiet > 0 && <Badge className="badge-warn border-transparent" data-testid="reengage-quiet">{t("usage.reengageQuiet", { n: draft.days_quiet })}</Badge>}
              {draft.history?.length > 0 && <span className="text-muted-foreground">{t("usage.reengageLast", { when: fmtWhen(draft.history[0].sent_at) })}</span>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reengage-to">{t("usage.reengageTo")}</Label>
              <Input id="reengage-to" value={draft.to.join(", ")} readOnly className="bg-secondary/50 font-mono text-xs" data-testid="reengage-to" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reengage-subject">{t("usage.reengageSubject")}</Label>
              <Input id="reengage-subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="reengage-subject" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reengage-body">{t("usage.reengageBody")}</Label>
              <Textarea id="reengage-body" value={body} onChange={(e) => setBody(e.target.value)} rows={14} className="font-mono text-xs leading-relaxed" data-testid="reengage-body" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="reengage-cancel">{t("common.cancel")}</Button>
          <Button onClick={send} disabled={!draft || sending || !subject.trim() || !body.trim()} data-testid="reengage-send">
            <Send className="mr-1.5 h-4 w-4" /> {sending ? t("usage.reengageSending") : t("usage.reengageSend")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/** Superadmin "are tenants using the platform?" view. */
export const PlatformUsageTab = () => {
  const { t } = useT();
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState(null);
  const [checking, setChecking] = useState(false);
  const [reengageOrg, setReengageOrg] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([getPlatformUsage(Number(days)), getDormantAlerts().catch(() => null)])
      .then(([usage, al]) => { setData(usage); setAlerts(al); })
      .catch(() => toast.error(t("usage.loadError")))
      .finally(() => setLoading(false));
  };
  useEffect(load, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  const runCheck = async () => {
    setChecking(true);
    try {
      const r = await runDormantTenantCheck();
      if (r.alerted?.length) toast.success(t("usage.dormantAlertedToast", { n: r.alerted.length }));
      if (r.resolved?.length) toast.info(t("usage.dormantResolvedToast", { n: r.resolved.length }));
      if (!r.alerted?.length && !r.resolved?.length) toast.info(t("usage.dormantNoneToast"));
      load();
    } catch {
      toast.error(t("usage.dormantRunError"));
    } finally {
      setChecking(false);
    }
  };

  const totals = data?.totals || { active: 0, low: 0, dormant: 0, never: 0 };
  const orgs = data?.organizations || [];
  const weeks = data?.trend_weeks || 8;
  const dormantDays = data?.dormant_days || 14;
  const COUNT_COLS = ["activity", "productions", "haccp_logs", "delivery_notes", "orders", "stock_lots", "milk_batches"];
  const COL_COUNT = 8 + COUNT_COLS.length;

  const weekLabel = (i) => (i === weeks - 1 ? t("usage.thisWeek") : t("usage.weekAgo", { n: weeks - 1 - i }));

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4" data-testid="usage-tab">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[
            ["active", Activity, "text-[hsl(var(--success))]"],
            ["low", Users, "text-[hsl(var(--warning))]"],
            ["dormant", Moon, "text-[hsl(var(--danger))]"],
            ["never", CircleOff, "text-muted-foreground"],
          ].map(([k, Icon, cls]) => (
            <Card key={k} data-testid={`usage-card-${k}`}>
              <CardContent className="p-4">
                <div className={`flex items-center gap-1.5 text-xs text-muted-foreground`}><Icon className={`h-4 w-4 ${cls}`} /> {t(`usage.status_${k}`)}</div>
                <div className="mt-1.5 font-mono text-2xl font-semibold tabular-nums" data-testid={`usage-total-${k}`}>{loading ? "…" : totals[k]}</div>
                <p className="text-[11px] text-muted-foreground">{t(`usage.hint_${k}`, { days })}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card data-testid="dormant-alert-card">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-md bg-secondary p-2 text-[hsl(var(--danger))]"><BellRing className="h-4 w-4" /></div>
              <div>
                <div className="text-sm font-semibold">{t("usage.dormantTitle")}</div>
                <p className="mt-0.5 max-w-2xl text-xs text-muted-foreground">
                  {t("usage.dormantHint", { hour: String(alerts?.schedule_hour_local ?? 8).padStart(2, "0"), days: dormantDays })}
                </p>
                <p className="mt-1 text-[11px] text-muted-foreground" data-testid="dormant-last-check">
                  {t("usage.dormantLastCheck", { when: alerts?.last_check?.created_at ? fmtWhen(alerts.last_check.created_at) : t("usage.dormantNever") })}
                  {alerts?.alerts?.length ? ` · ${alerts.alerts.filter((a) => a.state === "open").length} ${t("usage.dormantAlerted").toLowerCase()}` : ""}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={runCheck} disabled={checking || loading} data-testid="dormant-check-now">
              <Mail className={`mr-1.5 h-4 w-4 ${checking ? "animate-pulse" : ""}`} /> {checking ? t("usage.dormantRunning") : t("usage.dormantRun")}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base">{t("usage.title")}</CardTitle>
            <div className="flex items-center gap-2">
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="h-9 w-[150px]" data-testid="usage-days-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["7", "30", "90"].map((d) => <SelectItem key={d} value={d}>{t("usage.lastDays", { n: d })}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="usage-refresh">
                <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> {t("usage.refresh")}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("usage.colStatus")}</TableHead>
                    <TableHead>{t("usage.colOrg")}</TableHead>
                    <TableHead>{t("usage.colTrend", { n: weeks })}</TableHead>
                    <TableHead>{t("usage.colLogins", { n: weeks })}</TableHead>
                    <TableHead>{t("usage.colUsers")}</TableHead>
                    <TableHead>{t("usage.colLastLogin")}</TableHead>
                    <TableHead>{t("usage.colLastActivity")}</TableHead>
                    {COUNT_COLS.map((c) => <TableHead key={c} className="text-right">{t(`usage.count_${c}`)}</TableHead>)}
                    <TableHead className="text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                      <TableRow key={i}>{Array.from({ length: COL_COUNT }).map((__, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                    ))
                  ) : orgs.length === 0 ? (
                    <TableRow><TableCell colSpan={COL_COUNT} className="py-10 text-center text-sm text-muted-foreground">{t("usage.empty")}</TableCell></TableRow>
                  ) : orgs.map((o) => (
                    <TableRow key={o.id} data-testid={`usage-row-${o.id}`} className={o.status === "dormant" || o.status === "never" ? "opacity-80" : ""}>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-1">
                          <Badge className={`${STATUS_CLS[o.status]} border-transparent`} data-testid={`usage-status-${o.id}`}>{t(`usage.status_${o.status}`)}</Badge>
                          {o.billing_locked && <Badge className="badge-bad border-transparent text-[10px]">{t("superadmin.billingLocked")}</Badge>}
                          {o.dormant_alert_sent_at && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge className="badge-bad border-transparent text-[10px]" data-testid={`usage-dormant-alerted-${o.id}`}>
                                  <Mail className="mr-1 h-3 w-3" /> {t("usage.dormantAlerted")}
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent>{fmtWhen(o.dormant_alert_sent_at)}</TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{o.name || "—"}</div>
                        <div className="text-xs text-muted-foreground">
                          {o.admin_email}{o.plan_name ? ` · ${o.plan_name}` : ""}
                          {o.plan_name ? <span className={o.paid_plan ? " font-medium text-foreground/80" : ""}> ({o.paid_plan ? t("usage.paidPlan") : t("usage.freePlan")})</span> : null}
                          {" · "}{t("usage.since", { date: formatDate(o.created_at) })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <TrendCell values={o.weekly} total={o.weekly_total} weeks={weeks} weekLabel={weekLabel}
                          title={t("usage.trendTooltipTitle")} totalLabel={t("usage.trendTotal", { n: o.weekly_total ?? 0, weeks })}
                          emptyLabel={t("usage.trendEmpty", { weeks })} testId={`usage-trend-${o.id}`} />
                      </TableCell>
                      <TableCell>
                        <TrendCell values={o.weekly_logins} total={o.weekly_logins_total} weeks={weeks} weekLabel={weekLabel} neutral
                          title={t("usage.loginsTooltipTitle")} totalLabel={t("usage.loginsTotal", { n: o.weekly_logins_total ?? 0, weeks })}
                          emptyLabel={t("usage.loginsEmpty", { weeks })} testId={`usage-logins-${o.id}`} />
                      </TableCell>
                      <TableCell className="font-mono text-sm tabular-nums" data-testid={`usage-users-${o.id}`}>
                        <span className={o.active_users > 0 ? "font-semibold" : "text-muted-foreground"}>{o.active_users}</span>
                        <span className="text-muted-foreground"> / {o.user_count}</span>
                        <div className="text-[10px] text-muted-foreground">{t("usage.logins", { n: o.login_count })}</div>
                      </TableCell>
                      <TableCell className="text-xs">{fmtWhen(o.last_login)}</TableCell>
                      <TableCell className="text-xs">{fmtWhen(o.last_activity)}</TableCell>
                      {COUNT_COLS.map((c) => (
                        <TableCell key={c} className={`text-right font-mono text-sm tabular-nums ${o.counts?.[c] ? "" : "text-muted-foreground"}`}>{o.counts?.[c] ?? 0}</TableCell>
                      ))}
                      <TableCell className="text-right">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant={o.status === "active" ? "ghost" : "outline"} size="sm" className="h-8" onClick={() => setReengageOrg(o)} data-testid={`usage-reengage-${o.id}`}>
                              <Mail className="mr-1.5 h-3.5 w-3.5" /> {t("usage.reengage")}
                            </Button>
                          </TooltipTrigger>
                          {o.reengaged_at && <TooltipContent>{t("usage.reengageLast", { when: fmtWhen(o.reengaged_at) })}</TooltipContent>}
                        </Tooltip>
                        {o.reengaged_at && <div className="mt-0.5 text-[10px] text-muted-foreground" data-testid={`usage-reengaged-${o.id}`}>{t("usage.reengageLast", { when: formatDate(o.reengaged_at) })}</div>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">{t("usage.footnote")} {t("usage.loginsNote")}</p>
          </CardContent>
        </Card>
        <ReengageDialog org={reengageOrg} onClose={() => setReengageOrg(null)} onSent={load} />
      </div>
    </TooltipProvider>
  );
};
